/**
 * Polls & Voting API
 * 
 * API functions and React Query hooks for the polling system.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "./client";
import type {
  Poll,
  PollListItem,
  PollResults,
  CreatePollData,
  UpdatePollData,
  VotePollData,
  VoteResponse,
  ClosePollResponse,
} from "@/types/poll";

// Query key for React Query cache
export const POLLS_QUERY_KEY = "polls";

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get all polls (filtered by role on backend)
 */
export const listPolls = async (): Promise<PollListItem[]> => {
  const response = await apiClient.get<PollListItem[]>("/building/polls/");
  return response.data;
};

/**
 * Get a single poll by ID
 */
export const getPoll = async (id: string): Promise<Poll> => {
  const response = await apiClient.get<Poll>(`/building/polls/${id}/`);
  return response.data;
};

/**
 * Get all active polls
 */
export const listActivePolls = async (): Promise<PollListItem[]> => {
  const response = await apiClient.get<PollListItem[]>("/building/polls/active/");
  return response.data;
};

/**
 * Get all closed polls
 */
export const listClosedPolls = async (): Promise<PollListItem[]> => {
  const response = await apiClient.get<PollListItem[]>("/building/polls/closed/");
  return response.data;
};

/**
 * Create a new poll (HOA Admin only)
 */
export const createPoll = async (data: CreatePollData): Promise<Poll> => {
  const response = await apiClient.post<Poll>("/building/polls/", data);
  return response.data;
};

/**
 * Update a poll (HOA Admin only, options cannot be changed)
 */
export const updatePoll = async (
  id: string,
  data: UpdatePollData
): Promise<Poll> => {
  const response = await apiClient.put<Poll>(`/building/polls/${id}/`, data);
  return response.data;
};

/**
 * Delete a poll (HOA Admin only)
 */
export const deletePoll = async (id: string): Promise<void> => {
  await apiClient.delete(`/building/polls/${id}/`);
};

/**
 * Vote on a poll
 */
export const votePoll = async (
  id: string,
  data: VotePollData
): Promise<VoteResponse> => {
  const response = await apiClient.post<VoteResponse>(
    `/building/polls/${id}/vote/`,
    data
  );
  return response.data;
};

/**
 * Get poll results (visible after close or if show_results_before_close is true)
 */
export const getPollResults = async (id: string): Promise<PollResults> => {
  const response = await apiClient.get<PollResults>(
    `/building/polls/${id}/results/`
  );
  return response.data;
};

/**
 * Close a poll (HOA Admin only)
 */
export const closePoll = async (id: string): Promise<ClosePollResponse> => {
  const response = await apiClient.post<ClosePollResponse>(
    `/building/polls/${id}/close/`
  );
  return response.data;
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to fetch all polls
 */
export const usePolls = () => {
  return useQuery({
    queryKey: [POLLS_QUERY_KEY],
    queryFn: listPolls,
  });
};

/**
 * Hook to fetch a single poll
 */
export const usePoll = (id: string) => {
  return useQuery({
    queryKey: [POLLS_QUERY_KEY, id],
    queryFn: () => getPoll(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch active polls only
 */
export const useActivePolls = () => {
  return useQuery({
    queryKey: [POLLS_QUERY_KEY, "active"],
    queryFn: listActivePolls,
  });
};

/**
 * Hook to fetch closed polls only
 */
export const useClosedPolls = () => {
  return useQuery({
    queryKey: [POLLS_QUERY_KEY, "closed"],
    queryFn: listClosedPolls,
  });
};

/**
 * Hook to fetch poll results
 */
export const usePollResults = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [POLLS_QUERY_KEY, id, "results"],
    queryFn: () => getPollResults(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to create a poll
 */
export const useCreatePoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPoll,
    onSuccess: () => {
      // Invalidate all poll queries to refetch data
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    },
  });
};

/**
 * Hook to update a poll
 */
export const useUpdatePoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePollData }) =>
      updatePoll(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific poll and list queries
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    },
  });
};

/**
 * Hook to delete a poll
 */
export const useDeletePoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePoll,
    onSuccess: () => {
      // Invalidate all poll queries
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    },
  });
};

/**
 * Hook to vote on a poll
 */
export const useVotePoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VotePollData }) =>
      votePoll(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific poll and list queries to show updated vote counts
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, "active"] });
    },
  });
};

/**
 * Hook to close a poll
 */
export const useClosePoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: closePoll,
    onSuccess: (_, pollId) => {
      // Invalidate all poll queries
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, pollId] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, "active"] });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY, "closed"] });
    },
  });
};
